<script setup>
// Addendum §6 FileInput.
//   • Drag-and-drop AND an always-present <input type="file"> button —
//     drag-only is inaccessible (no keyboard equivalent).
//   • Full state machine: idle · drag-over · uploading (per-file progress) ·
//     success · error · partial. Upload progress is host-driven via `files`.
//   • CSV imports report ROW-LEVEL errors, not a single "Invalid file".
//
// SCOPE CONSTRAINT (do not widen without a product decision): FileInput is for
// CONFIGURATION, IMPORTS and IMAGES only — firm logo, theme assets, CSV imports.
// Lotus never ingests client documents; there is no attach-a-document
// affordance anywhere. A ticket asking for file attachment on a bucket is a
// product question, not a UI task.
import { ref, computed } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	accept: { type: String, default: null },
	multiple: { type: Boolean, default: false },
	hint: { type: String, default: 'Drag a file here, or browse. CSV, PNG or SVG.' },
	// host-owned upload state: [{ name, size, progress(0-100), status, error?, rowErrors?: [{row, message}] }]
	files: { type: Array, default: () => [] },
})
const emit = defineEmits(['add'])

const inputEl = ref(null)
const dragging = ref(false)

function pick() { inputEl.value?.click() }
function onChange(e) { if (e.target.files?.length) emit('add', e.target.files); e.target.value = '' }
function onDrop(e)
{
	e.preventDefault(); dragging.value = false
	if (e.dataTransfer?.files?.length) emit('add', e.dataTransfer.files)
}
function onDragOver(e) { e.preventDefault(); dragging.value = true }
function onDragLeave(e) { if (e.currentTarget.contains(e.relatedTarget)) return; dragging.value = false }

const fmtSize = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`)
const STATUS = {
	uploading: { icon: 'arrows-rotate', label: 'Uploading', cls: 'uploading' },
	success: { icon: 'circle-check', label: 'Done', cls: 'success' },
	error: { icon: 'circle-exclamation', label: 'Failed', cls: 'error' },
	partial: { icon: 'triangle-exclamation', label: 'Partial', cls: 'partial' },
}
</script>

<template>
	<div class="l-fileinput">
		<div class="dropzone" :class="{ dragging }" @dragover="onDragOver" @dragenter="onDragOver"
			@dragleave="onDragLeave" @drop="onDrop">
			<LIcon name="cloud-arrow-up" :size="24" class="dz-icon" />
			<p class="dz-hint">{{ hint }}</p>
			<button type="button" class="browse" @click="pick">Browse files</button>
			<input ref="inputEl" type="file" class="native" :accept="accept" :multiple="multiple" @change="onChange" />
		</div>

		<ul v-if="files.length" class="files">
			<li v-for="(f, i) in files" :key="i" class="file" :class="STATUS[f.status]?.cls">
				<LIcon :name="STATUS[f.status]?.icon || 'file'" :size="15" class="f-icon"
					:spin="f.status === 'uploading'" />
				<div class="f-main">
					<div class="f-top">
						<span class="f-name">{{ f.name }}</span>
						<span class="f-size num">{{ fmtSize(f.size) }}</span>
					</div>
					<div v-if="f.status === 'uploading'" class="f-track">
						<div class="f-fill" :style="{ width: (f.progress || 0) + '%' }" />
					</div>
					<div v-else-if="f.error" class="f-error">{{ f.error }}</div>
					<ul v-if="f.rowErrors && f.rowErrors.length" class="rowerrors">
						<li v-for="(re, j) in f.rowErrors" :key="j">
							<span class="num rowno">Row {{ re.row }}</span> — {{ re.message }}
						</li>
					</ul>
				</div>
				<span class="f-status">{{ STATUS[f.status]?.label }}</span>
			</li>
		</ul>
	</div>
</template>

<style scoped>
.l-fileinput
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-3);
}

.dropzone
{
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--sp-2);
	padding: var(--sp-7) var(--sp-5);
	border: 1.5px dashed var(--line-strong);
	border-radius: var(--r-lg);
	background: var(--surface-sunken);
	text-align: center;
	transition: border-color var(--t-fast) var(--ease), background var(--t-fast) var(--ease);
}

.dropzone.dragging
{
	border-color: var(--interactive);
	background: var(--interactive-tint);
}

.dz-icon
{
	color: var(--icon);
}

.dz-hint
{
	font-size: var(--text-sm);
	color: var(--text-3);
}

.browse
{
	font-family: var(--font-ui);
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--interactive-text);
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r-sm);
	padding: 6px 14px;
	min-height: var(--target-min);
	cursor: pointer;
}

.browse:hover
{
	background: var(--surface-sunken);
}

.native
{
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	border: 0;
}

.files
{
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: var(--sp-2);
}

.file
{
	display: flex;
	align-items: flex-start;
	gap: var(--sp-3);
	padding: 10px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
}

.f-icon
{
	margin-top: 2px;
	color: var(--icon);
}

.file.uploading .f-icon
{
	color: var(--interactive-text);
}

.file.success .f-icon
{
	color: var(--status-approved-text);
}

.file.error .f-icon
{
	color: var(--status-rejected-text);
}

.file.partial .f-icon
{
	color: var(--status-pending-text);
}

.f-main
{
	flex: 1;
	min-width: 0;
}

.f-top
{
	display: flex;
	justify-content: space-between;
	gap: var(--sp-3);
}

.f-name
{
	font-size: var(--text-base);
	font-weight: 500;
	color: var(--text);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.f-size
{
	font-size: var(--text-chip);
	color: var(--text-3);
}

.f-track
{
	height: 5px;
	background: var(--surface-sunken);
	border-radius: var(--r-pill);
	margin-top: 6px;
	overflow: hidden;
}

.f-fill
{
	height: 100%;
	background: var(--interactive);
	border-radius: var(--r-pill);
	transition: width var(--t-fast) var(--ease);
}

.f-error
{
	font-size: var(--text-chip);
	color: var(--status-rejected-text);
	margin-top: 4px;
}

.rowerrors
{
	list-style: none;
	margin-top: 6px;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.rowerrors li
{
	font-size: var(--text-chip);
	color: var(--text-2);
}

.rowerrors .rowno
{
	font-weight: 600;
	color: var(--status-rejected-text);
}

.f-status
{
	font-size: var(--text-micro);
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	color: var(--text-3);
	white-space: nowrap;
}
</style>
